import {
  Button,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormRow,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import "./validatePoints.scss";
import {
  Form,
  InputButtonGroup,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import { captureException } from "@sentry/react";
import { InfoCard } from "@src/components/infoCard/InfoCard";
import { LoaderOverlay } from "@src/components/loaderOverlay/LoaderOverlay";
import { NavButton } from "@src/components/navButton/NavButton";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import { displayToast } from "@src/components/toaster/Toaster";
import { StepProps } from "@src/Steps/step/Steps";
import { OuiNonOptions } from "@src/utils";
import axios from "axios";
import { useState } from "react";

export enum SituationPro {
  ACTIF = "SALARIE:STATUT_PROFESSIONNEL_TYPE",
  HORS_ACTIVITE = "SANS_PROFESSION:STATUT_PROFESSIONNEL_TYPE",
}

export const SOUHAIT_ADHERER =
  "Souhaitez-vous souscrire au contrat Paxivie que nous vous proposons ?";

const schema = zod.object({
  alreadyHaveContract: zod.boolean({ required_error: "Champ requis" }),
  souhaitRisqueAccident: zod.boolean({ required_error: "Champ requis" }),
  pratiqueDunSport: zod.boolean({ required_error: "Champ requis" }),
  souhaitAdherer: zod.boolean({ required_error: "Champ requis" }),
  situationPro: zod.nativeEnum(SituationPro, {
    required_error: "Champ requis",
  }),
});

export type ValidatePointsData = zod.infer<typeof schema>;

type ValidatePointsProps = StepProps<ValidatePointsData>;

export function ValidatePoints({
  changeStep,
  data,
  projectId,
  projetPreSouscription,
}: ValidatePointsProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formConfig = useFormConfig<typeof schema, ValidatePointsData>({
    schema,
    defaultValues: data,
  });

  if (!projetPreSouscription || !projectId) return null;

  const handleSubmit = async (submittedData: ValidatePointsData) => {
    const consentements = [];
    setIsSubmitting(true);

    if (submittedData.souhaitAdherer) {
      consentements.push({
        description: SOUHAIT_ADHERER,
        type: "SOUHAIT_ADHERER",
      });
    }

    try {
      await axios.put<unknown>(
        `//${process.env.VITE_URL_GATEWAY}/devis/${projectId}`,
        {
          mailAssure: projetPreSouscription.contact.mail,
          assure: {
            situationPro: submittedData?.situationPro,
            alreadyHasContract: submittedData?.alreadyHaveContract,
            pratiqueSportARisque: submittedData?.pratiqueDunSport,
          },
          consentements,
        }
      );
      changeStep("CHECK_YOUR_MAIL", "forward");
    } catch (err) {
      displayToast("Une erreur inconnue est survenue", "error");
      captureException(err);
      console.error(err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const alreadyHaveContract = formConfig.watch("alreadyHaveContract");
  const souhaitRisqueAccident = formConfig.watch("souhaitRisqueAccident");
  const pratiqueDunSport = formConfig.watch("pratiqueDunSport");
  const souhaitAdherer = formConfig.watch("souhaitAdherer");

  return (
    <>
      <LoaderOverlay isLoading={isSubmitting} />

      <div className="validate-step">
        <div className="stepper__centered-container">
          <div className="stepper__centered-content step__grid step__grid--padding-bottom">
            <NavButton
              onClick={() => {
                changeStep("SUBSCRIPTION_INTRODUCTION", "backward");
              }}
            >
              Précédent
            </NavButton>
            <h1 className="validate-step__title">
              Avant de poursuivre, merci de valider les points suivants
            </h1>
            <p className="validate-step__notice">
              <Emphasis>*</Emphasis> Champs obligatoires
            </p>
            <Form
              className="validate-step__form"
              formConfig={formConfig}
              onSubmit={handleSubmit}
            >
              <FormGrid>
                <FormRow>
                  <FormField className="validate-step__form-gap">
                    <InputButtonGroup
                      name="alreadyHaveContract"
                      size="large"
                      options={OuiNonOptions}
                      required
                      label="
                        Détenez-vous déjà un contrat qui vous couvre contre le
                        risque de séquelles physiques et psychiques faisant
                        suite à un accident de la vie courante et le décès
                        accidentel ?"
                    />
                    {alreadyHaveContract && (
                      <InfoCard>
                        <div>
                          <p>
                            Il vous est tout à fait possible de cumuler
                            plusieurs contrats couvrant ce même risque. Dans la
                            mesure où vous allez cotiser une seconde fois pour
                            ce même type de risque, il faut vous demander si le
                            bénéfice retiré est intéressant au regard des
                            cotisations dues au titre des deux contrats. Afin de
                            vous orienter au mieux, vous pouvez joindre nos
                            conseillers par téléphone du lundi au vendredi de 9h
                            à 12h30 et de 13h30 à 17h au : 0 969 398 399 (appel
                            non surtaxé).
                          </p>
                        </div>
                      </InfoCard>
                    )}
                  </FormField>
                </FormRow>
                {alreadyHaveContract !== undefined && (
                  <FormRow>
                    <FormField className="validate-step__form-gap">
                      <InputButtonGroup
                        name="souhaitRisqueAccident"
                        size="large"
                        options={OuiNonOptions}
                        required
                        label="
                          Souhaitez-vous, vous couvrir contre le risque accident
                          de la vie ?
                        "
                      />
                      {!souhaitRisqueAccident && (
                        <InfoCard>
                          <div>
                            <p>
                              Afin de continuer le parcours de souscription en
                              ligne, il faut que vous souhaitiez vous couvrir
                              contre le risque accident de la vie. Si vous avez
                              une question ou si vous souhaitez vous faire
                              accompagner par un conseiller, vous pouvez
                              demander à être rappelé.
                            </p>
                          </div>
                        </InfoCard>
                      )}
                    </FormField>
                  </FormRow>
                )}
                {souhaitRisqueAccident && (
                  <FormRow>
                    <FormField className="validate-step__form-gap">
                      <InputButtonGroup
                        name="pratiqueDunSport"
                        size="large"
                        options={OuiNonOptions}
                        label="
                          Pratiquez-vous une discipline sportive à contrainte
                          particulière et/ou qui s'exerce dans un
                          environnement spécifique ?
                        "
                      />
                      {pratiqueDunSport && (
                        <InfoCard>
                          <div>
                            Le contrat Paxivie ne vous couvre pas en cas
                            d&apos;accident résultant de la pratique d&apos;une
                            discipline sportive dite à contrainte particulière
                            et/ou qui s&apos;exerce dans un environnement
                            spécifique (alpinisme, spéléologie, parachutisme,
                            etc.). Retrouvez l&apos;ensemble des exclusions à
                            l&apos;article 16 du règlement Paxivie.
                          </div>
                        </InfoCard>
                      )}
                    </FormField>
                  </FormRow>
                )}
                {souhaitRisqueAccident && pratiqueDunSport !== undefined && (
                  <FormRow>
                    <FormField className="validate-step__form-gap">
                      <InputButtonGroup
                        name="souhaitAdherer"
                        size="large"
                        options={OuiNonOptions}
                        required
                        label={SOUHAIT_ADHERER}
                      />
                      {!souhaitAdherer && (
                        <InfoCard>
                          <div>
                            <p>
                              Afin de continuer le parcours de souscription en
                              ligne, il faut que vous souhaitiez souscrire au
                              contrat Paxivie que nous vous proposons. Si vous
                              avez une question ou si vous souhaitez vous faire
                              accompagner par un conseiller, vous pouvez
                              demander à être rappelé.
                            </p>
                          </div>
                        </InfoCard>
                      )}
                    </FormField>
                  </FormRow>
                )}
                {souhaitRisqueAccident && souhaitAdherer && (
                  <FormRow>
                    <FormField>
                      <InputButtonGroup
                        name="situationPro"
                        size="large"
                        options={[
                          { label: "En activité", value: SituationPro.ACTIF },
                          {
                            label: "Hors activité",
                            value: SituationPro.HORS_ACTIVITE,
                          },
                        ]}
                        required
                        label="Etes-vous en activité ou hors activité ?"
                      />
                    </FormField>
                  </FormRow>
                )}
                <FormAction>
                  <Button
                    type="submit"
                    disabled={
                      !souhaitRisqueAccident ||
                      !souhaitAdherer ||
                      !formConfig.formState.isValid ||
                      formConfig.formState.isSubmitting ||
                      formConfig.formState.isSubmitSuccessful
                    }
                  >
                    Valider
                  </Button>
                </FormAction>
              </FormGrid>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
